/**
 * Create a workspace (11-Authentication/Drafts/02-create-workspace-light-v3.png):
 * Details → Plan → Review. The caller becomes Owner (app.create_workspace). Billing is
 * not connected, so the Plan step lists only plans the server marks available and
 * otherwise says the workspace starts without a paid plan; nothing is charged.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft } from '../ui/glyphs.tsx';
import { WORKSPACE_NAME_MAX, classifyFailure, createWorkspace, initials, listPlans, type Account, type Plan } from '@miraops/contracts';
import { transport } from '../lib/session.ts';
import { useI18n } from '../lib/i18n.tsx';
import type { Prefs } from '../lib/prefs.ts';
import { Avatar, Banner, Button, Dropdown } from '../ui/ui.tsx';
import { Wordmark } from '../ui/icons.tsx';
import { HelpLink, LegalLinks, PrefsCluster } from '../auth/PublicChrome.tsx';
import { AccountButton } from './WorkspacePicker.tsx';
import {failureText} from '../lib/failure-text.ts';

type Step = 'details' | 'plan' | 'review';
const STEPS: Step[] = ['details', 'plan', 'review'];

/** "Pacific Time (America/Los_Angeles)" for each IANA zone the browser knows. */
function zoneOptions(locale: string): { value: string; label: string }[] {
  const zones: string[] = (Intl as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? ['UTC'];
  const name = (zone: string) => {
    try { return new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: 'longGeneric' }).formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value ?? zone; }
    catch { return zone; }
  };
  return zones.map(z => ({ value: z, label: `${name(z)} (${z})` }));
}

export function CreateWorkspace({ account, email, prefs, onPrefs, onSignOut, onCancel, onCreated }: {
  account: Account | null; email: string; prefs: Prefs; onPrefs: (p: Prefs) => void; onSignOut: () => void;
  onCancel: () => void; onCreated: (workspaceId: string) => void;
}) {
  const i18n = useI18n();
  const { t } = i18n;
  const [step, setStep] = useState<Step>('details');
  const [name, setName] = useState('');
  const [zone, setZone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [plan, setPlan] = useState<string>('none');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const requestId = useRef(crypto.randomUUID());
  const zones = useMemo(() => zoneOptions(i18n.locale), [i18n.locale]);
  const owner = account?.displayName || email;
  const validName = name.trim().length > 0 && name.trim().length <= WORKSPACE_NAME_MAX;

  // Plans are optional; when they can't be listed the Plan step says why (creating without one still works).
  const [plansFailure, setPlansFailure] = useState<string | null>(null);
  useEffect(() => { listPlans(transport).then(setPlans).catch(error => { setPlans([]); setPlansFailure(failureText(t, classifyFailure({ error, operation: 'read' }))); }); }, [t]);

  async function create() {
    setBusy(true); setFailure(null);
    try { onCreated(await createWorkspace(transport, { requestId: requestId.current, name, timeZone: zone })); }
    catch (error) { setFailure(failureText(t,classifyFailure({ error, operation: 'write' }))); setBusy(false); }
  }
  const next = () => setStep(STEPS[STEPS.indexOf(step) + 1]!);
  const index = STEPS.indexOf(step);

  return (
    <div className="public">
      <header className="chooser-head">
        <Wordmark size="lg" />
        <AccountButton account={account} email={email} onSignOut={onSignOut} />
      </header>
      <main className="create-ws" aria-labelledby="create-title">
        <button type="button" className="back-link" onClick={index === 0 ? onCancel : () => setStep(STEPS[index - 1]!)}>
          <ChevronLeft size={22} />{index === 0 ? t('ws.workspaces') : t('create.back')}
        </button>
        <h1 id="create-title">{t('create.title')}</h1>
        <ol className="stepper">
          {STEPS.map((s, i) => <li key={s} className={i < index ? 'done' : i === index ? 'on' : ''}><span className="dot" />{t(`create.step.${s}`)}</li>)}
        </ol>
        <div className="create-cols">
          <div className="create-form">
            {step === 'details' && (
              <>
                <label className="field">
                  <span className="field-label">{t('create.name')}</span>
                  <input className="field-input" value={name} maxLength={WORKSPACE_NAME_MAX} autoFocus onChange={e => setName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && validName) next(); }} />
                  <span className="hint">{t('create.nameHint')}</span>
                </label>
                <div className="field">
                  <span className="field-label">{t('create.timeZone')}</span>
                  <Dropdown className="wide" label={t('create.timeZone')} value={zone} onChange={setZone} options={zones} />
                </div>
              </>
            )}
            {step === 'plan' && (
              <div className="plan-list" role="radiogroup" aria-label={t('create.step.plan')}>
                {(plans ?? []).map(p => (
                  <label key={p.id} className={`plan-card${plan === p.id ? ' on' : ''}`}>
                    <input type="radio" name="plan" checked={plan === p.id} onChange={() => setPlan(p.id)} /><strong>{p.name}</strong>
                  </label>
                ))}
                <label className={`plan-card${plan === 'none' ? ' on' : ''}`}>
                  <input type="radio" name="plan" checked={plan === 'none'} onChange={() => setPlan('none')} />
                  <span><strong>{t('create.noPlan')}</strong><span className="hint">{t('create.noPlanHint')}</span></span>
                </label>
                {plans && plans.length > 0 && plan !== 'none' && <Banner tone="attention">{t('create.billingOff')}</Banner>}
                {plansFailure && <Banner tone="attention">{plansFailure}</Banner>}
              </div>
            )}
            {step === 'review' && (
              <dl className="review">
                <dt>{t('create.name')}</dt><dd>{name.trim()}</dd>
                <dt>{t('create.timeZone')}</dt><dd>{zones.find(z => z.value === zone)?.label ?? zone}</dd>
                <dt>{t('create.step.plan')}</dt><dd>{plan === 'none' ? t('create.noPlan') : plans?.find(p => p.id === plan)?.name}</dd>
                <dt>{t('create.role')}</dt><dd>{t('create.ownerRole')}</dd>
              </dl>
            )}
            {failure && <Banner tone="danger">{failure}</Banner>}
          </div>
          <aside className="create-owner">
            <h2>{t('create.owner')}</h2>
            <div className="owner-line"><Avatar text={initials(owner)} seed={email} size="lg" tone="gray" /><div><strong>{owner}</strong><span className="muted">{email}</span></div></div>
            <p className="muted">{t('create.ownerNote')}</p>
          </aside>
        </div>
      </main>
      <footer className="public-foot">
        <HelpLink />
        <PrefsCluster prefs={prefs} onPrefs={onPrefs} />
        <LegalLinks />
        <span className="spacer" />
        <Button size="lg" onClick={onCancel}>{t('export.cancel')}</Button>
        {step !== 'review'
          ? <Button size="lg" variant="primary" disabled={!validName} onClick={next}>
              {t(step === 'details' ? 'create.toPlans' : 'create.toReview')}</Button>
          : <Button size="lg" variant="primary" disabled={busy || !validName || plan !== 'none'} onClick={() => void create()}>{t(busy ? 'create.creating' : 'create.create')}</Button>}
      </footer>
    </div>
  );
}
